import Link from "next/link";
import useSWR from "swr";

import { useMemo } from "react";

import type { Account } from "models/account";
import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import ProfileCardButtons, {
  ProfileCardButtonsWithAuthentication,
} from "components/ProfileCardButtons";
import ProfileLogo from "components/ProfileLogo";
import ProfileSnippet from "components/ProfileSnippet";
import TextMore from "components/TextMore";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { ranks } from "consts";

import style from "./style.module.scss";

export const profileUrl = (profile: Account) => {
  const url = new ApiEndpoint(
    `/api/v2/${profile.isCompany ? "companies" : "accounts"}/${profile.slug}`
  );

  url.searchParams.append(
    "fields[accounts]",
    "slug,name,short-name,avatar-url,title,company-name,summary,areas-of-expertise,is-followed,is-insight-provider,activated"
  );
  url.searchParams.append(
    "fields[companies]",
    "slug,name,avatar-url,publish-under-company-name,title,analysts-count,summary,areas-of-expertise"
  );
  url.searchParams.append("no-view-count", "true");

  return url.href;
};

type ProfileCardProps = {
  insight: Insight;
  profile: any;
  isCompactMode?: boolean;
};

const ProfileCard = ({ insight, profile, isCompactMode }: ProfileCardProps) => {
  const { authenticated, account: loggedInAccount } = useAuthentication();

  const { data: additionalData } = useSWR(
    profile?.summary ? null : profileUrl(profile),
    baseFetcher
  );

  const media = useMediaQuery();

  const { slug, name, companyName, title, isCompany, avatarUrl } = profile;

  const summary = profile?.summary || additionalData?.summary;
  const expertiesTags = profile?.expertiesTags || additionalData?.expertiesTags;

  const ipRank = ranks[profile.ipRank];

  const authorURL = `/${isCompany ? "companies" : "profiles"}/${slug}`;

  const subProfile = useMemo(() => {
    if (profile.isCompany && insight.showAuthorNames) {
      return (
        <div className="ml-[10px]">
          <ProfileSnippet profile={insight.account} />
        </div>
      );
    }
  }, [insight.account, insight.showAuthorNames, profile.isCompany]);

  if (isCompactMode) {
    return (
      <div className={twMerge(style.profileRow, "justify-between")}>
        <div className="flex items-center justify-center gap-[10px]">
          <ProfileLogo
            size={media.mobile ? "small" : "compact"}
            profileURL={authorURL}
            logoSrc={avatarUrl?.small}
            isSquare={!!isCompany}
          />
          <div className={style.profileName}>
            <Link href={authorURL} passHref>
              <span>{name}</span>
            </Link>
          </div>
        </div>
        {loggedInAccount?.id !== profile?.id &&
          (authenticated ? (
            <ProfileCardButtonsWithAuthentication
              slug={slug}
              isCompany={profile.isCompany}
              buttonClassName={twMerge(
                "bg-white border border-solid border-smart-teal text-smart-teal",
                "md:hover:bg-smart-teal md:hover:text-white",
                "font-normal",
                "text-[11px] md:text-[12px]",
                "px-[6px] py-[5px] md:px-[10px] md:py-[6px]"
              )}
              buttonActiveClassName={twMerge("border-jade text-jade")}
              showIcon
              defaultMessage={`${insight.tagline}: <a href="${insight.shareableLinks?.direct}">${insight.shareableLinks?.direct}</a>`}
            />
          ) : (
            <ProfileCardButtons
              size={media.mobile ? "small" : "compact"}
              profile={profile}
            />
          ))}
      </div>
    );
  }

  return (
    <CustomCard additionalStyle={{ padding: 10 }}>
      <div className={style.container}>
        <div className={style.header}>
          <div className={style.profile_logo}>
            <ProfileLogo
              profileURL={authorURL}
              logoSrc={avatarUrl?.medium}
              isSquare={!!isCompany}
            />

            {ipRank?.isTopHalf && (
              <div className={style.ip_badge}>
                <span className={`${style.badge} ${style[ipRank.slug]}`}>
                  {ipRank.text}
                </span>
              </div>
            )}
          </div>
          <div className={style.profile_details}>
            <Link href={authorURL} passHref>
              <div className={twMerge(style.main_text, "truncate")}>{name}</div>
            </Link>

            <div
              className={`${style.sub_text} ${companyName && style.ellipsis}`}
            >
              {title}
            </div>
            <div className={`${style.sub_text} ${title && style.ellipsis}`}>
              {companyName}
            </div>

            <div className={style.tags}>
              {expertiesTags?.map?.((tag: string) => {
                return <span key={tag}>{tag}</span>;
              })}
            </div>
          </div>
        </div>

        {subProfile}

        {summary && authenticated && (
          <TextMore text={summary} limit={120} verticalArrow />
        )}

        <Link href={authorURL} passHref>
          <label className={style.link_text}>View Full Profile</label>
        </Link>

        {loggedInAccount?.id !== profile?.id &&
          (authenticated ? (
            <ProfileCardButtonsWithAuthentication
              slug={slug}
              subSlug={
                profile.isCompany && insight.showAuthorNames
                  ? insight.account.slug
                  : undefined
              }
              isCompany={profile.isCompany}
              className="justify-evenly py-[5px] px-[5px]"
              buttonClassName={twMerge(
                "flex-1",
                "bg-white border border-solid border-smart-teal text-smart-teal",
                "font-normal"
              )}
              buttonHoverClassName="hover:bg-smart-teal hover:text-white"
              buttonActiveClassName={twMerge("border-jade text-jade")}
              showIcon
              defaultMessage={`${insight.tagline}: <a href="${insight.shareableLinks?.direct}">${insight.shareableLinks?.direct}</a>`}
              alwaysShow
            />
          ) : (
            <ProfileCardButtons
              fullWidth={true}
              profile={profile}
              subProfile={
                profile.isCompany && insight.showAuthorNames
                  ? insight.account
                  : undefined
              }
            />
          ))}
      </div>
    </CustomCard>
  );
};

export default ProfileCard;
